import { FC, useEffect, useRef } from 'react';
import { BrowserQRCodeReader, IScannerControls } from '@zxing/browser';

export interface CameraScannerProps {
	onScan: (text: string) => void;
	onCameraError: (error: unknown) => void;
}

/*
 * One camera stream and one decode loop per mount, both stopped on unmount. react-qr-reader 3.0.0-beta-1
 * never stops either, so every Camera/Manual switch or result screen left another loop running against a
 * removed <video> until Safari froze and reloaded the tab.
 */
export const CameraScanner: FC<CameraScannerProps> = ({ onScan, onCameraError }) => {
	const videoRef = useRef<HTMLVideoElement>(null);
	const onScanRef = useRef(onScan);
	const onCameraErrorRef = useRef(onCameraError);
	onScanRef.current = onScan;
	onCameraErrorRef.current = onCameraError;

	useEffect(() => {
		let controls: IScannerControls | undefined;
		let stopped = false;
		const video = videoRef.current;
		if (!video) return undefined;

		new BrowserQRCodeReader()
			.decodeFromConstraints({ video: { facingMode: { ideal: 'environment' } } }, video, (result) => {
				if (!stopped && result) onScanRef.current(result.getText());
			})
			.then((started) => {
				if (stopped) started.stop();
				else controls = started;
			})
			.catch((error) => {
				if (!stopped) onCameraErrorRef.current(error);
			});

		return () => {
			stopped = true;
			controls?.stop();
		};
	}, []);

	return (
		<video
			ref={videoRef}
			muted
			playsInline
			style={{ width: '90%', height: 'auto', objectFit: 'cover', borderRadius: 8 }}
			data-testid="camera-preview"
		/>
	);
};
